import React, { useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Calendar,
  Check,
  ChevronRight,
  Info,
  Key,
  ListChecks,
  MapPin,
  Plus,
  Route as RouteIcon,
  Settings,
  Shield,
  Target,
  User,
  X,
} from "lucide-react";
import type {
  Question,
  QuestionAnswer,
  QuestionOption,
  QuestionsAnswerMap,
  QuestionsProposal,
} from "../lib/chatApi.js";

type IconComp = React.ComponentType<{ className?: string }>;

/** Icons for propose_questions rich card mode. Enum is defined on the
 *  tool schema; unknown icon falls back to a neutral chevron. */
const QUESTION_ICONS: Record<string, IconComp> = {
  page: ArrowRight as IconComp,
  settings: Settings as IconComp,
  person: User as IconComp,
  shield: Shield as IconComp,
  plus: Plus as IconComp,
  info: Info as IconComp,
  site: MapPin as IconComp,
  location: Target as IconComp,
  route: RouteIcon as IconComp,
  task: ListChecks as IconComp,
  role: Key as IconComp,
  schedule: Calendar as IconComp,
  incident: AlertTriangle as IconComp,
  alert: AlertCircle as IconComp,
  check: Check as IconComp,
  x: X as IconComp,
  calendar: Calendar as IconComp,
};

/** Sentinel value used when the operator clicks the auto-appended
 *  「其他」option. The free_text field carries the typed answer. */
const OTHER_VALUE = "__other__";

/** Per-question working state inside the card. Persists across renders
 *  until the operator hits submit (whole proposal collapses) or the
 *  operator dismisses the card. */
interface QuestionDraft {
  /** Set of value tokens currently picked. Single-select questions
   *  hold at most one entry; multi-select holds N. The「其他」option
   *  goes in as OTHER_VALUE — render checks this to decide whether to
   *  reveal the free-text input. */
  picked: Set<string>;
  /** Typed free-text when「其他」is in `picked`. Empty otherwise. */
  freeText: string;
}

function emptyDraft(): QuestionDraft {
  return { picked: new Set(), freeText: "" };
}

/** Inline multi-question picker rendered on an assistant bubble. One
 *  card per question; operator picks across all then taps a single
 *  submit button. On submit we collapse `answers` into the shape the
 *  AI tool_result protocol expects (single = object, multi = array)
 *  and hand off via onSubmit.
 *
 *  `resolved` flips the card to a read-only summary when a role='tool'
 *  message for this call_id already exists in chat history. We don't
 *  echo the picked values back (the tool_result is on a separate row
 *  not threaded here) — a generic 「已回覆」 is enough for scroll-back
 *  context. */
export default function PropQuestionsCard({
  proposal,
  resolved,
  dismissed,
  onSubmit,
  onDismiss,
}: {
  proposal: QuestionsProposal;
  resolved: boolean;
  dismissed: boolean;
  onSubmit: (callId: string, answers: QuestionsAnswerMap) => void;
  onDismiss: (callId: string) => void;
}): React.ReactElement {
  const [drafts, setDrafts] = useState<Map<number, QuestionDraft>>(() => {
    const seed = new Map<number, QuestionDraft>();
    proposal.questions.forEach((_, i) => seed.set(i, emptyDraft()));
    return seed;
  });
  const [submitting, setSubmitting] = useState(false);

  // Submit gating: every question must have at least one pick AND, if
  // 「其他」 is picked on a question, its free_text must be non-empty.
  // MUST stay above the `resolved / dismissed` early return — moving
  // it below makes the hook count change when the card flips to
  // read-only mode, triggering React error #300 ("rendered fewer
  // hooks than expected").
  const canSubmit = useMemo(() => {
    for (let i = 0; i < proposal.questions.length; i += 1) {
      const d = drafts.get(i) ?? emptyDraft();
      if (d.picked.size === 0) return false;
      if (d.picked.has(OTHER_VALUE) && d.freeText.trim().length === 0) return false;
    }
    return true;
  }, [drafts, proposal.questions]);

  if (resolved || dismissed) {
    return (
      <div className="rounded-md border border-border bg-bg-raised px-3 py-2 text-xs text-text-muted">
        <div className="font-medium text-text-secondary mb-0.5">
          {proposal.questions.length === 1
            ? proposal.questions[0]?.question
            : `${proposal.questions.length} 個問題`}
        </div>
        <div className="italic">{resolved ? "已回覆" : "已關閉"}</div>
      </div>
    );
  }

  const setDraft = (i: number, mutate: (d: QuestionDraft) => QuestionDraft): void => {
    setDrafts((prev) => {
      const next = new Map(prev);
      const cur = next.get(i) ?? emptyDraft();
      next.set(i, mutate({ picked: new Set(cur.picked), freeText: cur.freeText }));
      return next;
    });
  };

  const togglePick = (i: number, q: Question, val: string): void => {
    if (submitting) return;
    setDraft(i, (d) => {
      if (q.multiSelect) {
        if (d.picked.has(val)) d.picked.delete(val);
        else d.picked.add(val);
      } else {
        // Single-select: clicking the same option twice is a no-op
        // (deselecting would leave the question unanswered + force a
        // re-pick to submit — annoying). Different option swaps.
        d.picked.clear();
        d.picked.add(val);
      }
      // If「其他」 got removed, clear the free-text too — avoid
      // submitting orphan text. If it got added, keep prior text so
      // the operator can re-tick without retyping.
      if (!d.picked.has(OTHER_VALUE)) d.freeText = "";
      return d;
    });
  };

  const setFreeText = (i: number, text: string): void => {
    if (submitting) return;
    setDraft(i, (d) => {
      d.freeText = text;
      return d;
    });
  };

  const submit = (): void => {
    if (submitting || !canSubmit) return;
    setSubmitting(true);
    const answers: QuestionsAnswerMap = {};
    proposal.questions.forEach((q, i) => {
      const d = drafts.get(i) ?? emptyDraft();
      // Resolve picked tokens back to {value, label, free_text?}
      // objects in option order so the model sees a stable enumeration.
      // The auto-appended「其他」 is not in q.options — synthesize it
      // when picked.
      const resolvedAnswers: QuestionAnswer[] = [];
      for (const opt of q.options) {
        if (d.picked.has(opt.value)) {
          resolvedAnswers.push({ value: opt.value, label: opt.label });
        }
      }
      if (d.picked.has(OTHER_VALUE)) {
        resolvedAnswers.push({
          value: OTHER_VALUE,
          label: "其他",
          free_text: d.freeText.trim(),
        });
      }
      answers[q.question] = q.multiSelect ? resolvedAnswers : (resolvedAnswers[0] ?? { value: "", label: "" });
    });
    onSubmit(proposal.call_id, answers);
  };

  return (
    <div className="rounded-md border border-accent/40 bg-accent/5 p-2.5 flex flex-col gap-2.5">
      {proposal.questions.map((q, i) => {
        const draft = drafts.get(i) ?? emptyDraft();
        // Card layout engages when ANY option on THIS question has a
        // description / icon. Decided per-question so a 案場 picker
        // (rich) and 週期 picker (compact) can sit in the same call.
        const useCards = q.options.some((o) => o.description || o.icon);
        return (
          <div
            key={`${proposal.call_id}-q-${i}`}
            className="rounded-md border border-border bg-bg-surface px-3 py-2.5"
          >
            <div className="flex items-baseline gap-1.5 mb-1">
              <span className="inline-flex items-center rounded bg-accent/15 px-1.5 py-0.5 text-2xs font-medium text-accent">
                {q.header}
              </span>
            </div>
            {q.reason && (
              <div className="text-2xs text-text-muted mb-1">{q.reason}</div>
            )}
            <div className="text-xs font-medium text-text-primary mb-2">{q.question}</div>
            <PickerOptions
              question={q}
              draft={draft}
              useCards={useCards}
              submitting={submitting}
              onToggle={(val) => togglePick(i, q, val)}
              onFreeText={(text) => setFreeText(i, text)}
            />
          </div>
        );
      })}
      <div className="flex items-center justify-end gap-1.5 pt-0.5">
        <button
          type="button"
          onClick={() => { if (!submitting) onDismiss(proposal.call_id); }}
          disabled={submitting}
          className="rounded-md px-2 py-1 text-2xs text-text-muted hover:text-text-secondary disabled:opacity-50"
        >
          關閉
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !canSubmit}
          className="rounded-md bg-accent px-3 py-1.5 text-2xs font-medium text-bg-base hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          送出
        </button>
      </div>
    </div>
  );
}

/** Options renderer for one question. Picks compact-buttons vs card
 *  layout per `useCards`. Multi-select differs from single only in the
 *  ring/check indicator — picked state is the same Set in both cases. */
function PickerOptions({
  question,
  draft,
  useCards,
  submitting,
  onToggle,
  onFreeText,
}: {
  question: Question;
  draft: QuestionDraft;
  useCards: boolean;
  submitting: boolean;
  onToggle: (value: string) => void;
  onFreeText: (text: string) => void;
}): React.ReactElement {
  const otherActive = draft.picked.has(OTHER_VALUE);

  if (!useCards) {
    // Compact button row. Each option toggles instead of immediately
    // submitting (we wait
    // until all questions answered + submit button tapped).
    return (
      <div className="flex flex-wrap gap-1.5">
        {question.options.map((opt) => (
          <OptionPill
            key={opt.value}
            opt={opt}
            picked={draft.picked.has(opt.value)}
            multiSelect={question.multiSelect}
            disabled={submitting}
            onClick={() => onToggle(opt.value)}
          />
        ))}
        {question.allow_other && (
          <OtherPill
            picked={otherActive}
            disabled={submitting}
            onClick={() => onToggle(OTHER_VALUE)}
          />
        )}
        {otherActive && (
          <OtherFreeText
            value={draft.freeText}
            disabled={submitting}
            onChange={onFreeText}
          />
        )}
      </div>
    );
  }

  // Card mode: vertical list, each option a wider tap-target with
  // description / icon.
  return (
    <div className="flex flex-col gap-1.5">
      {question.options.map((opt) => (
        <OptionCard
          key={opt.value}
          opt={opt}
          picked={draft.picked.has(opt.value)}
          multiSelect={question.multiSelect}
          disabled={submitting}
          onClick={() => onToggle(opt.value)}
        />
      ))}
      {question.allow_other && (
        <OtherCard
          picked={otherActive}
          disabled={submitting}
          onClick={() => onToggle(OTHER_VALUE)}
        />
      )}
      {otherActive && (
        <OtherFreeText
          value={draft.freeText}
          disabled={submitting}
          onChange={onFreeText}
        />
      )}
    </div>
  );
}

function OptionPill({
  opt, picked, multiSelect, disabled, onClick,
}: {
  opt: QuestionOption; picked: boolean; multiSelect: boolean;
  disabled: boolean; onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={picked}
      className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs transition-colors disabled:opacity-50 ${
        picked
          ? "border-accent bg-accent/25 text-accent ring-1 ring-accent"
          : "border-accent/50 bg-accent/10 text-accent hover:bg-accent/20"
      }`}
    >
      {multiSelect && (
        <span className={`inline-block h-3 w-3 rounded-sm border ${picked ? "bg-accent border-accent" : "border-accent/60"}`}>
          {picked && <Check className="h-3 w-3 text-bg-base" />}
        </span>
      )}
      <span>{opt.label}</span>
    </button>
  );
}

function OtherPill({
  picked, disabled, onClick,
}: {
  picked: boolean; disabled: boolean; onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={picked}
      className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs transition-colors disabled:opacity-50 ${
        picked
          ? "border-accent bg-accent/15 text-accent"
          : "border-border bg-bg-raised text-text-secondary hover:bg-bg-surface"
      }`}
    >
      其他…
    </button>
  );
}

function OptionCard({
  opt, picked, multiSelect, disabled, onClick,
}: {
  opt: QuestionOption; picked: boolean; multiSelect: boolean;
  disabled: boolean; onClick: () => void;
}): React.ReactElement {
  const Icon = QUESTION_ICONS[opt.icon ?? ""] ?? (ChevronRight as IconComp);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={picked}
      className={`flex w-full items-start gap-2.5 rounded-md border px-3 py-2 text-left transition-colors disabled:opacity-50 ${
        picked
          ? "border-accent bg-accent/20 ring-1 ring-accent"
          : "border-accent/40 bg-accent/5 hover:bg-accent/15"
      }`}
    >
      {multiSelect && (
        <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${picked ? "bg-accent border-accent" : "border-accent/60"}`}>
          {picked && <Check className="h-3 w-3 text-bg-base" />}
        </span>
      )}
      <Icon className="h-4 w-4 mt-0.5 text-accent shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-text-primary">{opt.label}</div>
        {opt.description && (
          <div className="mt-0.5 text-2xs text-text-secondary line-clamp-2">{opt.description}</div>
        )}
      </div>
    </button>
  );
}

function OtherCard({
  picked, disabled, onClick,
}: {
  picked: boolean; disabled: boolean; onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={picked}
      className={`flex w-full items-center gap-2.5 rounded-md border px-3 py-2 text-left text-xs transition-colors disabled:opacity-50 ${
        picked
          ? "border-accent bg-accent/15 text-accent"
          : "border-border bg-bg-raised text-text-secondary hover:bg-bg-surface"
      }`}
    >
      <Plus className="h-4 w-4 text-text-muted shrink-0" />
      <span>其他…</span>
    </button>
  );
}

function OtherFreeText({
  value, disabled, onChange,
}: {
  value: string; disabled: boolean; onChange: (text: string) => void;
}): React.ReactElement {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="請輸入您的選擇或描述…"
      rows={2}
      maxLength={500}
      disabled={disabled}
      className="w-full resize-none rounded-md border border-border bg-bg-base px-2 py-1.5 text-xs text-text-primary placeholder-text-muted focus:border-accent focus:outline-none disabled:opacity-50"
    />
  );
}
